import React, { useEffect, useState } from "react";
import { useLaneCopy } from "../../utils/laneCopy";
import { useAppLanguage } from "../../utils/appLanguage";
import i18n from "../../i18n";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// ─── Types (mirrors backend services/sajuService.js response) ─────────────────

interface Pillar {
  stem: string;
  branch: string;
}

interface SajuResponse {
  pillars: {
    year: Pillar | null;
    month: Pillar | null;
    day: Pillar | null;
    hour: Pillar | null;
  };
  elements: {
    fire: number;
    water: number;
    wood: number;
    metal: number;
    earth: number;
    dominant: string;
    weak: string;
    summary: string;
  };
  yinYang: {
    yin: number;
    yang: number;
    balance: string;
    summary: string;
  };
  personality: {
    temperament: string;
    emotionalFlow: string;
    coreNature: string;
  };
  destinyFlow: {
    foundation: string;
    presentSeason: string;
    growthEdge: string;
  };
  dailyLuck: {
    energy: string;
    advice: string;
    caution: string;
  } | null;
  summary: string;
}

interface ReadingData {
  readingId: string;
  lang?: string;
  saju_response: SajuResponse;
}

// ─── Warm "hanji paper" palette — client-approved (cream/beige, muted sage,
// dusty rose, tan) replacing the earlier lavender scheme for the result page.

const KR_FONT = "'Noto Sans KR', sans-serif";
const C = {
  bg: "linear-gradient(160deg, #F6EEE4 0%, #EFE3D3 100%)",
  card: "#FBF6EE",
  cardBorder: "#E6D9C4",
  accent: "#A8A08C",
  accentSoft: "rgba(168,160,140,0.14)",
  accentText: "#6B5A3E",
  textDark: "#3A3226",
  textBody: "#5C5140",
  textMuted: "#9C8F78",
  success: "#EFF1E4",
  successText: "#556B2F",
  successBorder: "#C3CCA6",
};

// Visual-only metadata (hanja glyph + colors are language-independent);
// the human-readable label comes from ResultStrings.
const ELEMENT_META: Record<
  string,
  { hanja: string; color: string; soft: string }
> = {
  wood: { hanja: "木", color: "#7C9070", soft: "#DCE3D3" },
  fire: { hanja: "火", color: "#B4574A", soft: "#E9CFC7" },
  earth: { hanja: "土", color: "#BC9A54", soft: "#EBDFBE" },
  metal: { hanja: "金", color: "#D8D2C4", soft: "#F4F1E9" },
  water: { hanja: "水", color: "#5E7A94", soft: "#D6E0E8" },
};

// Generative cycle order (오행 상생): Wood → Fire → Earth → Metal → Water → Wood
const ELEMENT_CYCLE = ["wood", "fire", "earth", "metal", "water"] as const;

const PILLAR_META: Record<
  string,
  { label: { en: string; ko: string }; hanja: string; tone: string }
> = {
  year: { label: { en: "Year Pillar", ko: "연주" }, hanja: "年", tone: "#DCE3D3" },
  month: { label: { en: "Month Pillar", ko: "월주" }, hanja: "月", tone: "#EBDFBE" },
  day: { label: { en: "Day Pillar", ko: "일주" }, hanja: "日", tone: "#E9CFC7" },
  hour: { label: { en: "Hour Pillar", ko: "시주" }, hanja: "時", tone: "#F4F1E9" },
};

// ─── Localization ──────────────────────────────────────────────────────────────
// The backend (sajuController.js) writes the reading in the user's preferred
// language and echoes it back as `lang`; the page chrome follows that same
// preference through useLaneCopy.

interface ResultStrings {
  headerTitle: string;
  backButton: string;
  openingSummary: string;
  fourPillars: {
    title: string;
    subtitle: string;
    unavailable: string;
    pillarSub: { year: string; month: string; day: string; hour: string };
  };
  fiveElements: {
    title: string;
    subtitle: string;
    dominant: string;
    weak: string;
    elementLabel: {
      wood: string;
      fire: string;
      earth: string;
      metal: string;
      water: string;
    };
  };
  yinYang: {
    title: string;
    yin: string;
    yang: string;
    balance: string;
    balanceLabel: { balanced: string; "yin-heavy": string; "yang-heavy": string };
  };
  personality: {
    title: string;
    subtitle: string;
    temperament: string;
    emotionalFlow: string;
    coreNature: string;
  };
  destinyFlow: {
    title: string;
    subtitle: string;
    foundation: string;
    presentSeason: string;
    growthEdge: string;
  };
  dailyLuck: {
    title: string;
    energy: string;
    advice: string;
    caution: string;
  };
  endingNote: string;
  newReadingButton: string;
  footer: string;
  noDataError: string;
  loadError: string;
}

const STRINGS: Record<"en" | "ko", ResultStrings> = {
  en: {
    headerTitle: "Saju Reading",
    backButton: "← Back",
    openingSummary: "Your Saju reading — quietly read, honestly told.",
    fourPillars: {
      title: "Four Pillars",
      subtitle:
        "Year, Month, Day, and Hour — the four columns of your destiny.",
      unavailable: "unavailable",
      pillarSub: { year: "Year", month: "Month", day: "Day", hour: "Hour" },
    },
    fiveElements: {
      title: "Five Elements",
      subtitle:
        "How wood, fire, earth, metal, and water cycle through your chart.",
      dominant: "dominant — solid fill",
      weak: "weak — dashed ring",
      elementLabel: {
        wood: "Wood",
        fire: "Fire",
        earth: "Earth",
        metal: "Metal",
        water: "Water",
      },
    },
    yinYang: {
      title: "Yin-Yang Balance",
      yin: "Yin",
      yang: "Yang",
      balance: "Balance",
      balanceLabel: {
        balanced: "Balanced",
        "yin-heavy": "Yin Heavy",
        "yang-heavy": "Yang Heavy",
      },
    },
    personality: {
      title: "Personality",
      subtitle: "How your Saju shows up in daily life.",
      temperament: "Temperament",
      emotionalFlow: "Emotional Flow",
      coreNature: "Core Nature",
    },
    destinyFlow: {
      title: "Destiny Flow",
      subtitle: "The arc from foundation to what's quietly unfolding now.",
      foundation: "Foundation",
      presentSeason: "Present Season",
      growthEdge: "Growth Edge",
    },
    dailyLuck: {
      title: "Today's Flow",
      energy: "Energy",
      advice: "Advice",
      caution: "Caution",
    },
    endingNote:
      "Your Saju is a quiet map, not a fixed fate — it names a tendency, not a verdict.",
    newReadingButton: "New Saju Reading",
    footer: "a quiet read on your destiny — not a fortune carved in stone",
    noDataError: "No reading data found.",
    loadError: "Failed to load your reading.",
  },
  ko: {
    headerTitle: "사주팔자 Saju Reading",
    backButton: "← 뒤로",
    openingSummary: "당신의 사주를 조용히, 정직하게 읽어드립니다.",
    fourPillars: {
      title: "사주팔자",
      subtitle: "년, 월, 일, 시 — 당신의 운명을 구성하는 네 기둥입니다.",
      unavailable: "정보 없음",
      pillarSub: { year: "년", month: "월", day: "일", hour: "시" },
    },
    fiveElements: {
      title: "오행 균형",
      subtitle:
        "당신의 차트에서 목, 화, 토, 금, 수가 어떻게 순환하는지 보여줍니다.",
      dominant: "주된 기운 — 채워진 원",
      weak: "부족한 기운 — 점선 원",
      elementLabel: {
        wood: "목",
        fire: "화",
        earth: "토",
        metal: "금",
        water: "수",
      },
    },
    yinYang: {
      title: "음양 균형",
      yin: "음",
      yang: "양",
      balance: "균형",
      balanceLabel: {
        balanced: "균형",
        "yin-heavy": "음 강함",
        "yang-heavy": "양 강함",
      },
    },
    personality: {
      title: "기질",
      subtitle: "당신의 사주가 일상에서 어떻게 드러나는지 보여줍니다.",
      temperament: "기질",
      emotionalFlow: "감정의 흐름",
      coreNature: "본질",
    },
    destinyFlow: {
      title: "운세 흐름",
      subtitle: "기초부터 지금 펼쳐지고 있는 흐름까지의 궤적입니다.",
      foundation: "기초",
      presentSeason: "현재의 계절",
      growthEdge: "성장의 가장자리",
    },
    dailyLuck: {
      title: "오늘의 기운",
      energy: "에너지",
      advice: "조언",
      caution: "주의사항",
    },
    endingNote:
      "사주는 정해진 운명이 아닌 조용한 지도입니다 — 경향을 알려줄 뿐, 판결을 내리지 않습니다.",
    newReadingButton: "새 사주 읽기",
    footer: "당신의 운명을 조용히 들여다보는 것일 뿐, 정해진 미래가 아닙니다",
    noDataError: "읽기 데이터를 찾을 수 없습니다.",
    loadError: "읽기를 불러오지 못했습니다.",
  },
};

// ─── Animation variants (mirrors MatescanResult.tsx) ───────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.07,
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// ─── Shared UI atoms ────────────────────────────────────────────────────────

const SectionCard: React.FC<{
  title: string;
  subtitle?: string;
  index?: number;
  children: React.ReactNode;
}> = ({ title, subtitle, index = 0, children }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
    className="rounded-3xl p-5 sm:p-6 md:p-8 mb-5 shadow-sm border"
    style={{ background: C.card, borderColor: C.cardBorder }}
  >
    <div className="flex items-center gap-2 mb-1">
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ background: C.accent }}
      />
      <h3
        className="text-[10px] md:text-xs font-semibold tracking-[0.2em] uppercase"
        style={{ color: C.accentText }}
      >
        {title}
      </h3>
    </div>
    {subtitle && (
      <p
        className="text-[11px] md:text-xs mb-5 ml-3.5"
        style={{ color: C.textMuted }}
      >
        {subtitle}
      </p>
    )}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </motion.div>
);

const PillarCard: React.FC<{
  label: string;
  sub: string;
  hanja: string;
  tone: string;
  pillar: Pillar | null;
  unavailableText: string;
}> = ({ label, sub, hanja, tone, pillar, unavailableText }) => (
  <div
    className="rounded-2xl p-4 text-center flex flex-col items-center gap-1.5"
    style={{ background: pillar ? tone : "#F1ECE1" }}
  >
    <p
      className="text-sm font-semibold"
      style={{ color: C.textDark, fontFamily: KR_FONT }}
    >
      {label}
    </p>
    <p
      className="text-[10px] uppercase tracking-wide"
      style={{ color: C.textBody, opacity: 0.75 }}
    >
      {sub}
    </p>
    {pillar ? (
      <>
        <p
          className="text-xl font-medium mt-1"
          style={{ color: C.textDark, fontFamily: KR_FONT }}
        >
          {pillar.stem}
          {pillar.branch}
        </p>
        <span className="text-base opacity-60" style={{ color: C.textBody }}>
          {hanja}
        </span>
      </>
    ) : (
      <p className="text-[11px] mt-2" style={{ color: C.textMuted }}>
        {unavailableText}
      </p>
    )}
  </div>
);

// Circular Five Elements diagram — nodes placed around a ring in the
// generative-cycle order (Wood → Fire → Earth → Metal → Water → Wood), with
// curved arrows tracing that cycle, mirroring the reference layout.
const ElementCycleDiagram: React.FC<{
  elements: Pick<
    SajuResponse["elements"],
    "fire" | "water" | "wood" | "metal" | "earth"
  >;
  dominant: string;
  weak: string;
  t: ResultStrings;
}> = ({ elements, dominant, weak, t }) => {
  const size = 240;
  const center = size / 2;
  const ringRadius = 82;
  const nodeRadius = 30;

  const positions = ELEMENT_CYCLE.map((key, i) => {
    // start at top (-90deg) and go clockwise through the generative cycle
    const angle = -90 + (360 / ELEMENT_CYCLE.length) * i;
    const rad = (angle * Math.PI) / 180;
    return {
      key,
      x: center + ringRadius * Math.cos(rad),
      y: center + ringRadius * Math.sin(rad),
      angle,
    };
  });

  const arcPath = (
    from: (typeof positions)[number],
    to: (typeof positions)[number],
  ) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const mx = (from.x + to.x) / 2;
    const my = (from.y + to.y) / 2;
    // bow the midpoint outward from center for a gentle curve
    const nx = mx - center;
    const ny = my - center;
    const nlen = Math.sqrt(nx * nx + ny * ny) || 1;
    const bow = dist * 0.18;
    const cx = mx + (nx / nlen) * bow;
    const cy = my + (ny / nlen) * bow;
    return `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`;
  };

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <marker
            id="saju-arrow"
            markerWidth="7"
            markerHeight="7"
            refX="5.5"
            refY="3.5"
            orient="auto"
          >
            <path d="M0,0 L7,3.5 L0,7 Z" fill={C.textMuted} opacity={0.6} />
          </marker>
        </defs>
        {positions.map((p, i) => {
          const next = positions[(i + 1) % positions.length];
          // trim the line short of each node's edge so the arrowhead is visible
          const dx = next.x - p.x;
          const dy = next.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const fromX = p.x + (dx / dist) * (nodeRadius + 2);
          const fromY = p.y + (dy / dist) * (nodeRadius + 2);
          const toX = next.x - (dx / dist) * (nodeRadius + 6);
          const toY = next.y - (dy / dist) * (nodeRadius + 6);
          return (
            <path
              key={`arc-${p.key}`}
              d={arcPath(
                { ...p, x: fromX, y: fromY },
                { ...next, x: toX, y: toY },
              )}
              fill="none"
              stroke={C.textMuted}
              strokeWidth={1.4}
              opacity={0.4}
              markerEnd="url(#saju-arrow)"
            />
          );
        })}
        {positions.map((p) => {
          const meta = ELEMENT_META[p.key];
          const value = elements[p.key] ?? 0;
          const isDominant = dominant === p.key;
          const isWeak = weak === p.key;
          return (
            <g key={p.key}>
              <circle
                cx={p.x}
                cy={p.y}
                r={nodeRadius}
                fill={isDominant ? meta.color : meta.soft}
                stroke={isWeak ? meta.color : "transparent"}
                strokeWidth={isWeak ? 2 : 0}
                strokeDasharray={isWeak ? "3 3" : undefined}
                opacity={isWeak && !isDominant ? 0.75 : 1}
              />
              <text
                x={p.x}
                y={p.y - 3}
                textAnchor="middle"
                fontSize="17"
                fontFamily={KR_FONT}
                fill={isDominant ? "#FBF6EE" : C.textDark}
              >
                {meta.hanja}
              </text>
              <text
                x={p.x}
                y={p.y + 13}
                textAnchor="middle"
                fontSize="9"
                fontWeight={600}
                fill={isDominant ? "#FBF6EE" : C.textBody}
                opacity={0.85}
              >
                {value}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 mt-2">
        {ELEMENT_CYCLE.map((key) => {
          const meta = ELEMENT_META[key];
          return (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: meta.color }}
              />
              <span
                className="text-[10px] font-medium"
                style={{ color: C.textBody }}
              >
                {t.fiveElements.elementLabel[key]}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3">
        <div className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ background: C.accentText }}
          />
          <span className="text-[10px]" style={{ color: C.textMuted }}>
            {t.fiveElements.dominant}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0 border-2 border-dashed"
            style={{ borderColor: C.textMuted }}
          />
          <span className="text-[10px]" style={{ color: C.textMuted }}>
            {t.fiveElements.weak}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const SajuResult: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ReadingData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const appLanguage = useAppLanguage();
  const isKorean = appLanguage === "ko";
  const t = useLaneCopy("sajuresult", STRINGS);

  useEffect(() => {
    const raw = sessionStorage.getItem("sajuReading");
    if (!raw) {
      setError(t.noDataError);
      return;
    }
    try {
      setData(JSON.parse(raw));
    } catch {
      setError(t.loadError);
    }
  }, [t]);

  const handleNewReading = () => {
    sessionStorage.removeItem("sajuReading");
    navigate("/saju");
  };

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.bg, fontFamily: KR_FONT }}
      >
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: "#BE123C" }}>
            {error}
          </p>
          <button
            onClick={() => navigate("/saju")}
            className="px-6 py-2.5 rounded-full text-white text-xs font-semibold"
            style={{
              background: `linear-gradient(to right, ${C.accent}, ${C.accentText})`,
            }}
          >
            {t.newReadingButton}
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: C.bg }}
      >
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: `${C.accent}33`, borderTopColor: C.accent }}
        />
      </div>
    );
  }

  const {
    pillars,
    elements,
    yinYang,
    personality,
    destinyFlow,
    dailyLuck,
    summary,
  } = data.saju_response;

  return (
    <div
      className="min-h-screen w-full"
      style={{ background: C.bg, fontFamily: KR_FONT }}
    >
      <div className="h-1 w-full" />

      <div className="min-h-screen w-full px-4 sm:px-6 py-8 sm:py-12">
        <div className="w-full max-w-2xl mx-auto">
          {/* Header nav */}
          <div className="mb-8 relative flex items-center justify-center min-h-[40px]">
            <div className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: C.accent }}
              />
              <span
                className="text-[10px] md:text-xs tracking-[0.2em] uppercase font-medium"
                style={{ color: C.textMuted }}
              >
                {t.headerTitle}
              </span>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: C.accent }}
              />
            </div>
            <button
              onClick={handleNewReading}
              className="absolute right-0 inline-flex items-center gap-1.5 sm:gap-2 rounded-full border bg-white px-4 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.15em] shadow-sm transition flex-shrink-0"
              style={{ borderColor: C.cardBorder, color: C.accentText }}
            >
              {t.backButton}
            </button>
          </div>

          <div className="space-y-5">
            {/* ── OPENING SUMMARY ── */}
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="rounded-3xl p-6 sm:p-8 text-center shadow-sm border"
              style={{ background: C.accentSoft, borderColor: C.cardBorder }}
            >
              <div className="text-3xl sm:text-4xl mb-4">命</div>
              <p
                className="text-sm sm:text-base leading-relaxed max-w-md mx-auto"
                style={{ color: C.textBody }}
              >
                {summary || t.openingSummary}
              </p>
            </motion.div>

            {/* ── FOUR PILLARS ── */}
            <SectionCard
              title={t.fourPillars.title}
              subtitle={t.fourPillars.subtitle}
              index={1}
            >
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(["year", "month", "day", "hour"] as const).map((key) => (
                  <PillarCard
                    key={key}
                    label={isKorean ? PILLAR_META[key].label.ko : i18n.t(`saju.pillar.${key}`)}
                    sub={t.fourPillars.pillarSub[key]}
                    hanja={PILLAR_META[key].hanja}
                    tone={PILLAR_META[key].tone}
                    pillar={pillars[key]}
                    unavailableText={t.fourPillars.unavailable}
                  />
                ))}
              </div>
            </SectionCard>

            {/* ── FIVE ELEMENTS ── */}
            <SectionCard
              title={t.fiveElements.title}
              subtitle={t.fiveElements.subtitle}
              index={2}
            >
              <ElementCycleDiagram
                elements={elements}
                dominant={elements.dominant}
                weak={elements.weak}
                t={t}
              />
              {elements.summary && (
                <p
                  className="text-xs md:text-sm leading-relaxed mt-5 pt-4 border-t"
                  style={{ color: C.textBody, borderColor: C.cardBorder }}
                >
                  {elements.summary}
                </p>
              )}
            </SectionCard>

            {/* ── YIN-YANG ── */}
            <SectionCard title={t.yinYang.title} index={3}>
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="flex-1 rounded-2xl p-4 text-center border"
                  style={{ background: "#F1EAD8", borderColor: C.cardBorder }}
                >
                  <p
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: C.textMuted }}
                  >
                    {t.yinYang.yin}
                  </p>
                  <p
                    className="text-2xl font-medium mt-1"
                    style={{ color: C.textDark }}
                  >
                    {yinYang.yin}
                  </p>
                </div>
                <div
                  className="flex-1 rounded-2xl p-4 text-center border"
                  style={{ background: "#F1EAD8", borderColor: C.cardBorder }}
                >
                  <p
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: C.textMuted }}
                  >
                    {t.yinYang.yang}
                  </p>
                  <p
                    className="text-2xl font-medium mt-1"
                    style={{ color: C.textDark }}
                  >
                    {yinYang.yang}
                  </p>
                </div>
                <div
                  className="flex-1 rounded-2xl p-4 text-center border"
                  style={{
                    background: C.accentSoft,
                    borderColor: C.cardBorder,
                  }}
                >
                  <p
                    className="text-[10px] font-semibold uppercase tracking-wide"
                    style={{ color: C.accentText }}
                  >
                    {t.yinYang.balance}
                  </p>
                  <p
                    className="text-xs font-medium mt-2"
                    style={{ color: C.accentText }}
                  >
                    {t.yinYang.balanceLabel[
                      yinYang.balance as keyof typeof t.yinYang.balanceLabel
                    ] || yinYang.balance.replace("-", " ")}
                  </p>
                </div>
              </div>
              {yinYang.summary && (
                <p
                  className="text-xs md:text-sm leading-relaxed"
                  style={{ color: C.textBody }}
                >
                  {yinYang.summary}
                </p>
              )}
            </SectionCard>

            {/* ── PERSONALITY ── */}
            <SectionCard
              title={t.personality.title}
              subtitle={t.personality.subtitle}
              index={4}
            >
              <div className="space-y-3">
                {[
                  {
                    label: t.personality.temperament,
                    text: personality.temperament,
                  },
                  {
                    label: t.personality.emotionalFlow,
                    text: personality.emotionalFlow,
                  },
                  {
                    label: t.personality.coreNature,
                    text: personality.coreNature,
                  },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-2xl p-3.5 border"
                    style={{ background: "#F1EAD8", borderColor: C.cardBorder }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                      style={{ background: C.accent }}
                    />
                    <div>
                      <p
                        className="text-[10px] font-semibold uppercase tracking-wide mb-0.5"
                        style={{ color: C.accentText }}
                      >
                        {row.label}
                      </p>
                      <p
                        className="text-xs md:text-sm leading-relaxed"
                        style={{ color: C.textBody }}
                      >
                        {row.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── DESTINY FLOW ── */}
            <SectionCard
              title={t.destinyFlow.title}
              subtitle={t.destinyFlow.subtitle}
              index={5}
            >
              <div className="space-y-3">
                {[
                  {
                    label: t.destinyFlow.foundation,
                    text: destinyFlow.foundation,
                  },
                  {
                    label: t.destinyFlow.presentSeason,
                    text: destinyFlow.presentSeason,
                  },
                  {
                    label: t.destinyFlow.growthEdge,
                    text: destinyFlow.growthEdge,
                  },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-2xl p-3.5 border"
                    style={{ background: "#F1EAD8", borderColor: C.cardBorder }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                      style={{ background: C.accentText }}
                    />
                    <div>
                      <p
                        className="text-[10px] font-semibold uppercase tracking-wide mb-0.5"
                        style={{ color: C.accentText }}
                      >
                        {row.label}
                      </p>
                      <p
                        className="text-xs md:text-sm leading-relaxed"
                        style={{ color: C.textBody }}
                      >
                        {row.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* ── DAILY LUCK (optional) ── */}
            {dailyLuck && (
              <SectionCard title={t.dailyLuck.title} index={6}>
                <div className="space-y-3">
                  {[
                    { label: t.dailyLuck.energy, text: dailyLuck.energy },
                    { label: t.dailyLuck.advice, text: dailyLuck.advice },
                    { label: t.dailyLuck.caution, text: dailyLuck.caution },
                  ].map((row, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 rounded-2xl p-3.5 border"
                      style={{
                        background: "#F1EAD8",
                        borderColor: C.cardBorder,
                      }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                        style={{ background: C.accent }}
                      />
                      <div>
                        <p
                          className="text-[10px] font-semibold uppercase tracking-wide mb-0.5"
                          style={{ color: C.accentText }}
                        >
                          {row.label}
                        </p>
                        <p
                          className="text-xs md:text-sm leading-relaxed"
                          style={{ color: C.textBody }}
                        >
                          {row.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* ── ENDING NOTE ── */}
            <motion.div
              custom={7}
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              className="rounded-3xl p-6 sm:p-8 text-center shadow-sm border"
              style={{ background: C.success, borderColor: C.successBorder }}
            >
              <p
                className="text-sm sm:text-base font-medium leading-relaxed"
                style={{ color: C.successText }}
              >
                {t.endingNote}
              </p>
            </motion.div>
          </div>

          {/* Footer actions */}
          <div className="flex justify-center mt-10 pb-4">
            <button
              onClick={handleNewReading}
              className="w-full sm:w-auto py-3.5 px-12 rounded-full text-xs font-semibold uppercase tracking-[0.15em] text-white shadow-lg transition-all duration-200 hover:opacity-90 transform hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(to right, ${C.accent}, ${C.accentText})`,
              }}
            >
              {t.newReadingButton}
            </button>
          </div>

          <div
            className="text-center mt-3 pb-8 text-[10px] md:text-[11px] tracking-wide"
            style={{ color: C.textMuted }}
          >
            <span
              className="inline-block border-t w-10 sm:w-16 mx-2 align-middle"
              style={{ borderColor: `${C.accent}40` }}
            />
            {t.footer}
            <span
              className="inline-block border-t w-10 sm:w-16 mx-2 align-middle"
              style={{ borderColor: `${C.accent}40` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SajuResult;
